import { type RefObject, useLayoutEffect } from 'react';

import { placeFloating } from './anchor-position';

export interface AnchoredPositionOptions {
  /** 기준 요소와 상자 사이(px) */
  gap: number;
  /** 꺼져 있으면(닫힘 · 제자리 그리기) 아무것도 하지 않는다. 기본 true */
  enabled?: boolean;
  /** 상자 너비를 기준 요소와 같게(Select 목록) */
  matchWidth?: boolean;
  /**
   * 남은 공간보다 크면 상자 높이를 줄인다(style.maxHeight — 안의 목록이 스크롤한다).
   * 끄면 뒤집기 · 가로 맞춤만(달력처럼 줄이면 안 되는 상자)
   */
  limitHeight?: boolean;
}

/**
 * portal + fixed 로 띄운 상자를 기준 요소에 붙인다(placeFloating — 아래 공간이 모자라면 위로).
 * 스크롤 · 창 크기 · 상자 · 기준 요소 크기가 바뀌면 다시 잡는다. 상자에 `data-placement` 를 단다.
 * 상자는 처음에 `invisible fixed top-0 left-0` 로 그려 두면 자리를 잡은 뒤 보인다.
 */
export function useAnchoredPosition(
  anchorRef: RefObject<HTMLElement | null>,
  floatingRef: RefObject<HTMLElement | null>,
  { gap, enabled = true, matchWidth = false, limitHeight = false }: AnchoredPositionOptions,
): void {
  useLayoutEffect(() => {
    if (!enabled) return;
    const update = () => {
      const anchor = anchorRef.current;
      const floating = floatingRef.current;
      if (!anchor || !floating) return;
      const rect = anchor.getBoundingClientRect();
      if (matchWidth) floating.style.width = `${rect.width}px`;
      // 줄이기 전 본래 크기로 잰다
      if (limitHeight) floating.style.maxHeight = '';
      const size = floating.getBoundingClientRect();
      const place = placeFloating({
        anchor: rect,
        floating: { width: size.width, height: size.height },
        viewport: {
          width: document.documentElement.clientWidth || window.innerWidth,
          height: window.innerHeight,
        },
        gap,
      });
      if (limitHeight) floating.style.maxHeight = `${place.maxHeight}px`;
      floating.style.top = `${place.top}px`;
      floating.style.left = `${place.left}px`;
      floating.dataset.placement = place.placement;
      floating.style.visibility = 'visible';
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    // 목록을 받는 중 · 검색으로 줄어드는 등 크기가 바뀌면 다시
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    if (anchorRef.current) observer?.observe(anchorRef.current);
    if (floatingRef.current) observer?.observe(floatingRef.current);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      observer?.disconnect();
    };
  }, [enabled, gap, matchWidth, limitHeight, anchorRef, floatingRef]);
}
